/**
 * FRED (Federal Reserve Economic Data) source.
 * Fetches key economic indicators: GDP growth, unemployment rate,
 * CPI inflation, and federal funds rate.
 * Requires FRED API key (free from https://fred.stlouisfed.org/docs/api/api_key.html).
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'fred';

/** FRED series IDs for key indicators. */
const TRACKED_SERIES = [
  { seriesId: 'GDP', label: 'GDP (Quarterly)', unit: 'Billions $' },
  { seriesId: 'UNRATE', label: 'Unemployment Rate', unit: '%' },
  { seriesId: 'CPIAUCSL', label: 'CPI (All Urban)', unit: 'Index 1982-84=100' },
  { seriesId: 'FEDFUNDS', label: 'Federal Funds Rate', unit: '%' },
  { seriesId: 'T10Y2Y', label: '10Y-2Y Treasury Spread', unit: '%' },
  { seriesId: 'DEXUSEU', label: 'USD/EUR Exchange Rate', unit: 'USD per EUR' },
] as const;

interface FredObservation {
  date: string;
  value: string;
}

function indicatorToSeverity(seriesId: string, value: number): Severity {
  // Flag abnormal readings — check highest thresholds first
  if (seriesId === 'UNRATE' && value > 8) return 'critical';
  if (seriesId === 'UNRATE' && value > 6) return 'high';
  if (seriesId === 'FEDFUNDS' && value > 7) return 'critical';
  if (seriesId === 'FEDFUNDS' && value > 5) return 'high';
  if (seriesId === 'FEDFUNDS' && value > 3) return 'medium';
  if (seriesId === 'T10Y2Y' && value < -0.5) return 'critical';
  if (seriesId === 'T10Y2Y' && value < 0) return 'high'; // Inverted yield curve
  return 'info';
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const fredSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'FRED Economic Data',
    description: 'Federal Reserve Economic Data — GDP, unemployment, CPI, interest rates',
    icon: 'landmark',
    category: 'finance',
    updateInterval: 3600_000, // 1 hour
    requiresApiKey: true,
    apiKeyUrl: 'https://fred.stlouisfed.org/docs/api/api_key.html',
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    {
      key: 'apiKey',
      label: 'FRED API Key',
      type: 'password',
      required: true,
      helpText: 'Free API key from fred.stlouisfed.org',
    },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] FRED source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/fred`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`FRED API returned ${response.status}`);
      }

      const data: { series: { seriesId: string; observations: FredObservation[] }[] } = await response.json();

      if (!data.series || !Array.isArray(data.series)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const series of data.series) {
        const meta = TRACKED_SERIES.find(s => s.seriesId === series.seriesId);
        if (!meta) continue;

        // Take the most recent observation (first element when sort_order=desc)
        const obs = series.observations;
        if (!obs || obs.length === 0) continue;

        const latest = obs[0];
        const value = parseFloat(latest.value);
        if (isNaN(value)) continue;

        // Calculate change from previous if available
        let change: number | undefined;
        if (obs.length >= 2) {
          const prev = parseFloat(obs[1].value);
          if (!isNaN(prev) && prev !== 0) {
            change = value - prev;
          }
        }

        events.push({
          id: `fred-${series.seriesId}-${latest.date}`,
          source: SOURCE_ID,
          timestamp: new Date(latest.date).toISOString(),
          type: 'economic',
          title: `${meta.label}: ${value.toFixed(2)} ${meta.unit}${change !== undefined ? ` (${change >= 0 ? '+' : ''}${change.toFixed(2)})` : ''}`,
          severity: indicatorToSeverity(series.seriesId, value),
          location: {
            lat: 38.89,
            lng: -77.04,
            name: 'Washington, DC',
            country: 'United States',
            countryCode: 'US',
          },
          tags: ['economic', 'fred', series.seriesId.toLowerCase()],
          raw: {
            seriesId: series.seriesId,
            value,
            date: latest.date,
            unit: meta.unit,
            change,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
